import React, { useContext, useEffect } from "react";
import { AxiosError } from "axios";
import { useForm } from "@mantine/form";
import { Space } from "@mantine/core";
import { Navigate } from "react-router-dom";

import { queryClient } from "../../../App";
import { AuthContext } from "../../../providers/AuthProvider";
import { HttpClientError } from "../../../providers/HttpClient";
import { useToasts } from "../../../providers/notifications/ToastsContext";
import useUpdateTagMetadataApiService from "../services/api/UpdateTagMetadataApiService";
import TagCategorySelect from "../elements/TagCategorySelect";
import TagNameInput from "./TagNameInput";
import TagFormActions from "./TagFormActions";

interface TagMetadataFormProps {
  tagId: number;
  initialName: string;
  initialCategory: string;
  onSuccess?: () => void;
  onFailure?: () => void;
}

const TagMetadataForm: React.FC<TagMetadataFormProps> = ({
  tagId,
  initialName,
  initialCategory,
  onSuccess,
  onFailure,
}) => {
  const auth = useContext(AuthContext);
  const { showToast } = useToasts();

  const {
    isLoading: isUpdateLoading,
    isSuccess: isUpdateSuccess,
    isError: isUpdateFailure,
    error: updateError,
    mutate: onUpdate,
  } = useUpdateTagMetadataApiService();

  const failureNotification = (err: AxiosError | null): void =>
    showToast({
      strapiId: "tag_submission_failed",
      data: {
        message: HttpClientError(err),
      },
    });

  useEffect(() => {
    if (isUpdateFailure) {
      failureNotification(updateError);
      if (onFailure) {
        onFailure();
      }
    }
  }, [isUpdateFailure, updateError, onFailure]);

  useEffect(() => {
    if (isUpdateSuccess) {
      // Close form and show toast immediately
      if (onSuccess) {
        onSuccess();
      }
      showToast({ strapiId: "tags_category_updated" });

      // Invalidate ALL categorized tag queries (any search term)
      queryClient.invalidateQueries(["TagsCategoryListApiService"]);

      // Also invalidate the simple tag list (used by dropdowns)
      queryClient.invalidateQueries(["TagsListApiService"]);

      // Optional: invalidate this specific tag's details
      queryClient.invalidateQueries(["TagsDetailsApiService", tagId]);
    }
  }, [isUpdateSuccess]);

  const form = useForm({
    initialValues: {
      name: initialName,
      category: initialCategory,
    },
  });

  const onSubmitHandler = (formValues: typeof form.values): void => {
    onUpdate({
      id: tagId,
      name: formValues.name,
      category: formValues.category,
    });
  };

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <form
      onSubmit={form.onSubmit((formValues) => onSubmitHandler(formValues))}
      style={{ width: "100%" }}
    >
      <TagNameInput form={form} />
      <Space h="xl" />
      <TagCategorySelect form={form} />
      <Space h="xl" />
      <TagFormActions isLoading={isUpdateLoading} buttonText="Update Tag" />
    </form>
  );
};

export default TagMetadataForm;
